import React from 'react'
import StickyBox from 'react-sticky-box'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import type { HolidayPackagesIndexEntry } from '@/src/types/holidays'
import type { HolidayPackageSort } from '@/src/constants/holidays'
import type { HolidayPackageFacet, HolidayPackageFilters } from '@/src/utils/holidays/packageFilters'
import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import HolidaysFooter from '@/src/components/desktop/homepageFooter'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'

import HolidayBreadcrumbsWrapper from '@/src/components/common/holidays/holidayBreadcrumbs'
import PackageSortControlWrapper from '@/src/components/common/holidays/packageSortControl'
import PackagesFilterPanelWrapper from '@/src/components/common/holidays/packagesFilterPanel'
import PackagesResultsGrid from '@/src/components/common/holidays/packagesResultsGrid/packagesResultsGrid'
import PackagesSearchBarWrapper from '@/src/components/common/holidays/packagesSearchBar'
import PackageThemeChipsWrapper from '@/src/components/common/holidays/packageThemeChips'
import ContactStripWrapper from '@/src/components/common/holidays/contactStrip'
import { HOLIDAYS_MAX_WIDTH, VISUALLY_HIDDEN } from '@/src/utils/holidays'

/** Width of the filter rail, matched to the reference design. */
const FILTER_RAIL_WIDTH = 288

interface HolidayPackagesDesktopPageProps {
    heading: string
    breadcrumbLabel: string
    resultCountLabel: string
    emptyTitle: string
    emptyBody: string
    clearAllLabel: string
    packages: HolidayPackagesIndexEntry[]
    cityFacets: HolidayPackageFacet[]
    monthFacets: HolidayPackageFacet[]
    occasionFacets: HolidayPackageFacet[]
    themeFacets: HolidayPackageFacet[]
    filters: HolidayPackageFilters
    totalPackages: number
    hasActiveFilters: boolean
    sort: HolidayPackageSort
    onSortChange: (sort: HolidayPackageSort) => void
    onToggleCity: (key: string) => void
    onToggleMonth: (key: string) => void
    onToggleOccasion: (key: string) => void
    onToggleTheme: (key: string) => void
    onApplySearch: (cities: string[], months: string[]) => void
    onClearAll: () => void
    domainCountry: string
}

const HolidayPackagesDesktopPage: React.FC<HolidayPackagesDesktopPageProps> = ({
    heading,
    breadcrumbLabel,
    resultCountLabel,
    emptyTitle,
    emptyBody,
    clearAllLabel,
    packages,
    cityFacets,
    monthFacets,
    occasionFacets,
    themeFacets,
    filters,
    totalPackages,
    hasActiveFilters,
    sort,
    onSortChange,
    onToggleCity,
    onToggleMonth,
    onToggleOccasion,
    onToggleTheme,
    onApplySearch,
    onClearAll,
    domainCountry
}) => {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'clip'
            }}
        >
            <ErrorBoundaryWrapper identifier="holidays-packages-header-desktop">
                <HeaderContainer domainCountry={domainCountry} activeVertical="holidays" />
            </ErrorBoundaryWrapper>

            <Container component="main" maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH, py: 3 }}>
                <ErrorBoundaryWrapper identifier="holidays-packages-breadcrumbs-desktop">
                    <HolidayBreadcrumbsWrapper currentLabel={breadcrumbLabel} />
                </ErrorBoundaryWrapper>

                {/* The page still needs one h1 for search engines and screen readers; the design
                    has no room for it, so it is read but not drawn. */}
                <Typography component="h1" sx={VISUALLY_HIDDEN}>
                    {heading}
                </Typography>

                <Box sx={{ mt: 2 }}>
                    <ErrorBoundaryWrapper identifier="holidays-packages-themes-desktop">
                        <PackageThemeChipsWrapper
                            themeFacets={themeFacets}
                            filters={filters}
                            onToggle={onToggleTheme}
                            isMobile={false}
                        />
                    </ErrorBoundaryWrapper>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <ErrorBoundaryWrapper identifier="holidays-packages-searchbar-desktop">
                        <PackagesSearchBarWrapper
                            cityFacets={cityFacets}
                            monthFacets={monthFacets}
                            filters={filters}
                            totalPackages={totalPackages}
                            onApply={onApplySearch}
                            isMobile={false}
                        />
                    </ErrorBoundaryWrapper>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, mt: 3, alignItems: 'flex-start' }}>
                    {/*
                     * Same rail behaviour as the flights SRP, from the same component: the rail
                     * is taller than the window, so it scrolls up with the results until its last
                     * filter is on screen and pins there. A plain `top` sticky would leave the
                     * foot of the rail permanently below the fold, and a `bottom` sticky does not
                     * engage on the way down — the offset depends on the rail's own height, which
                     * CSS cannot read, so StickyBox measures it.
                     */}
                    <Box
                        sx={{
                            width: FILTER_RAIL_WIDTH,
                            flexShrink: 0,
                            // The row is aligned to its top, which would size this column to the
                            // rail itself and leave the sticky element no distance to travel.
                            alignSelf: 'stretch'
                        }}
                    >
                        <StickyBox offsetTop={16} offsetBottom={16}>
                            <ErrorBoundaryWrapper identifier="holidays-packages-filters-desktop">
                                <PackagesFilterPanelWrapper
                                    cityFacets={cityFacets}
                                    monthFacets={monthFacets}
                                    occasionFacets={occasionFacets}
                                    themeFacets={themeFacets}
                                    filters={filters}
                                    onToggleCity={onToggleCity}
                                    onToggleMonth={onToggleMonth}
                                    onToggleOccasion={onToggleOccasion}
                                    onToggleTravelStyle={onToggleTheme}
                                    onClearAll={onClearAll}
                                    isMobile={false}
                                />
                            </ErrorBoundaryWrapper>
                        </StickyBox>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
                        >
                            <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'textColors.primary' }}>
                                {resultCountLabel}
                            </Typography>
                            <PackageSortControlWrapper value={sort} onChange={onSortChange} isMobile={false} />
                        </Stack>

                        {packages.length ? (
                            <ErrorBoundaryWrapper identifier="holidays-packages-grid-desktop">
                                <PackagesResultsGrid
                                    packages={packages}
                                    sourceSection="all-packages"
                                    isMobile={false}
                                />
                            </ErrorBoundaryWrapper>
                        ) : (
                            <Stack spacing={1} sx={{ py: 8, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '1.125rem', fontWeight: 700 }}>{emptyTitle}</Typography>
                                <Typography sx={{ fontSize: '0.938rem', color: 'textColors.subheading' }}>
                                    {emptyBody}
                                </Typography>
                                {hasActiveFilters && (
                                    <Box>
                                        <Button onClick={onClearAll} sx={{ mt: 1, fontWeight: 700 }}>
                                            {clearAllLabel}
                                        </Button>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </Box>
                </Box>

                <Box sx={{ mt: 5 }}>
                    <ErrorBoundaryWrapper identifier="holidays-packages-contact-desktop">
                        <ContactStripWrapper layout="inline" isMobile={false} />
                    </ErrorBoundaryWrapper>
                </Box>
            </Container>

            <Box sx={{ mt: 'auto' }}>
                <HolidaysFooter showHomeHighlights={false} />
            </Box>
        </Box>
    )
}

export { HolidayPackagesDesktopPage }
export default HolidayPackagesDesktopPage
